/**
 * Read per-window selection / state for a Repancy desktop window.
 *
 * Dispatches by path → looks up the matching `window.__repancy*`
 * bridge → returns its read-only snapshot. Missing bridges return a
 * structured `{ available: false, reason }`, never throw.
 *
 * The set of windows with bridges is intentionally smaller than the
 * set NIPE can `open`. Windows whose state is fully captured by the
 * URL + focused note (e.g. Help) don't need a bridge.
 */

import type { NipeTool } from './types';
import { NIPE_KNOWN_WINDOWS } from './openWindow';

export interface ReadWindowContextContext {
  /** Snapshot reader from useNipeChat. Receives the window path,
   * returns the bridge's current snapshot or null when no bridge is
   * registered. Leaf — never touches the DOM directly. */
  readWindowContext?: (path: string) => unknown | null;
}

const KNOWN_PATHS = NIPE_KNOWN_WINDOWS.map((w) => w.path);

export const readWindowContextTool: NipeTool<ReadWindowContextContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'readWindowContext',
      description:
        'Read the per-window selection / state snapshot from a Repancy ' +
        'desktop window. Returns what the user currently has selected, ' +
        'focused, or unsaved inside that window. Use this when the user ' +
        'asks about "what am I looking at" or you need to ground a ' +
        'recommendation in their current state. Not every window has a ' +
        'bridge — windows without one return `{available: false}`. ' +
        'Bridges currently shipped: /obsidian-vault, /documents, ' +
        '/settings, /skills, /prompt-lab, /corpus-search, /clients, ' +
        '/obsidian-import. /help has no state worth reading.',
      parameters: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Window path. One of: ' + KNOWN_PATHS.join(', '),
          },
        },
        required: ['path'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.readWindowContext) {
      return {
        available: false,
        reason: 'readWindowContext integration is not available in this session.',
      };
    }
    const path = typeof args.path === 'string' ? args.path : '';
    if (!KNOWN_PATHS.includes(path)) {
      return {
        available: false,
        reason: `Unknown window path "${path}". Known: ${KNOWN_PATHS.join(', ')}`,
      };
    }
    const snap = ctx.readWindowContext(path);
    if (snap === null || snap === undefined) {
      return {
        available: false,
        reason: `No selection-state bridge is registered for ${path}.`,
      };
    }
    return { available: true, path, state: snap };
  },
};
